import { ButtonUtils, ObjectBuilder, Image3D, Mesh2D, MeshUtils } from "@dreamcam/babylon-tools";
import { MeshName } from "../MeshName";
import { DcRenderingGroup } from "../DcRenderingGroup";
import { createVector3 } from "../ButtonAnimation";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel.js";
import { Env } from "../../settings/Env";
import { AppView } from "../AppView";
import { BroadcastManagerState } from "@dreamcam/web-sdk";
import { Container } from "@babylonjs/gui/2D/controls/container.js";
import { LayoutComponentHelper } from "./LayoutComponentHelper";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { text } from "../../TextResources";
import { delayAsync } from "../../utils/AsyncUtils";
import { LoginDialog } from "./LoginDialog";
import { InfoDialog } from "./InfoDialog";
import { MessageType } from "../../interfaces/NotificationCenter";
import { XRInputHelper } from "../XRInputHelper";
const screenDelayMillis = 500;
const thankyouDelayMillis = 1500;
const stateToProfileImageColor = {
    [BroadcastManagerState.OFFLINE]: Env.videoClip.lockedVideoPanel.purchase.model.profilePhoto.status.background.offline,
    [BroadcastManagerState.PUBLIC]: Env.videoClip.lockedVideoPanel.purchase.model.profilePhoto.status.background.live,
    [BroadcastManagerState.MY_PRIVATE_SHOW]: Env.videoClip.lockedVideoPanel.purchase.model.profilePhoto.status.background.live,
    [BroadcastManagerState.SPY_SHOW]: Env.videoClip.lockedVideoPanel.purchase.model.profilePhoto.status.background.private,
    [BroadcastManagerState.AWAY]: Env.videoClip.lockedVideoPanel.purchase.model.profilePhoto.status.background.away,
    [BroadcastManagerState.PRIVATE_SHOW]: Env.videoClip.lockedVideoPanel.purchase.model.profilePhoto.status.background.private,
};
export class LockedVideoCoverPanel {
    constructor(callbacks) {
        this._currentBalance = 0;
        this._videoClip = null;
        this._callbacks = callbacks;
        const appView = AppView.getInstance();
        const lv = Env.videoClip.lockedVideoPanel;
        this._mesh2d = MeshUtils.createPlaneMesh(MeshName.lockedVideoCoverPanel, lv.width, lv.height, appView.scene, appView.guiRootNode, DcRenderingGroup.default, true);
        this._mesh2d.mesh.position = createVector3(lv.position);
        this._mesh2d.isVisible = false;
        this._controls = this._createControls();
        this._controls.panelPurchase.self.isVisible = false;
        this._dialogs = this._createDialogs();
        const extraMeshPointerAddon = XRInputHelper.addPointerAddon(this._mesh2d.mesh);
        extraMeshPointerAddon.onPointerOut.add(() => this._mesh2d.notifyChildrenOnPointerOut());
        // this._mesh2d.notifyChildrenOnPointerOut();
    }
    _showInternal(screenType) {
        this._controls.panelPurchase.self.isVisible =
            screenType === "unlock" || screenType === "buy" || screenType === "unlockToBuyTransition";
        this._controls.panelPurchase.panelUnlockButtons.isVisible = screenType === "unlock";
        this._controls.panelPurchase.panelBuyButtons.isVisible = screenType === "buy";
        this._controls.panelFinal.isVisible = screenType === "final";
    }
    show() {
        if (this._videoClip === null || this._videoClip.isFree || this._videoClip.product?.isPurchased === true) {
            this.hide();
            return false;
        }
        this._mesh2d.isVisible = true;
        this._showInternal("unlock");
        return true;
    }
    hide() {
        if (this._dialogs.login?.isVisible === true) {
            this._dialogs.login.hide();
        }
        this._mesh2d.isVisible = false;
    }
    _createControls() {
        const lv = Env.videoClip.lockedVideoPanel;
        const addVerticalGapControl = (parent, height) => {
            const gap = new Control(`lockedVideoCoverVerticalGap${Math.round(height * 1000)}`);
            gap.widthInPixels = 1;
            gap.heightInPixels = this._mesh2d.m2px(height);
            parent.addControl(gap);
        };
        const backgroundImage = new Image3D("lockedVideoCoverImage");
        // backgroundImage.blur = lv.cover.blur;
        this._mesh2d.texture.addControl(backgroundImage);
        const overlay = new Container("lockedVideoCoverImageOverlay");
        overlay.background = lv.cover.overlayColor;
        overlay.onPointerClickObservable.add(() => {
            this._callbacks.switchMainMenuVisibility();
        });
        this._mesh2d.texture.addControl(overlay);
        {
            const p = lv.purchase;
            var stackPanelPurchase = new StackPanel("lockedVideoCoverStackPanelPurchase");
            // stackPanelPurchase.background = "red";
            // stackPanelPurchase.adaptWidthToChildren = true;
            stackPanelPurchase.isVertical = true;
            stackPanelPurchase.horizontalAlignment = StackPanel.HORIZONTAL_ALIGNMENT_CENTER;
            stackPanelPurchase.verticalAlignment = StackPanel.VERTICAL_ALIGNMENT_TOP;
            stackPanelPurchase.topInPixels = this._mesh2d.m2px(p.top);
            stackPanelPurchase.onPointerClickObservable.add(() => {
                this._callbacks.switchMainMenuVisibility();
            });
            this._mesh2d.texture.addControl(stackPanelPurchase);
            var titleTextBlock = ObjectBuilder.createTextBlock3D(this._mesh2d, "lockedVideoCoverTitle", "", p.title);
            stackPanelPurchase.addControl(titleTextBlock);
            addVerticalGapControl(stackPanelPurchase, lv.verticalGaps.gap32);
            {
                const stackPanelModelLine = new StackPanel("lockedVideoCoverStackPanelModelLine");
                stackPanelModelLine.isVertical = false;
                stackPanelModelLine.heightInPixels = this._mesh2d.m2px(p.model.height);
                stackPanelPurchase.addControl(stackPanelModelLine);
                var statusControls = LayoutComponentHelper.createModelProfileControl(this._mesh2d, null, p.model.profilePhoto);
                statusControls.rootContainer.widthInPixels = this._mesh2d.m2px(p.model.profilePhoto.photoStatusContainerHeight);
                stackPanelModelLine.addControl(statusControls.rootContainer);
                var modelNicknameTextBlock = ObjectBuilder.createTextBlock3D(this._mesh2d, "lockedVideoCoverModel", "", p.model.modelNickname);
                stackPanelModelLine.addControl(modelNicknameTextBlock);
            }
            addVerticalGapControl(stackPanelPurchase, lv.verticalGaps.gap48);
            {
                var stackPanelPriceLine = new StackPanel("lockedVideoCoverStackPanelBalanceLine");
                stackPanelPriceLine.isVertical = false;
                stackPanelPriceLine.heightInPixels = this._mesh2d.m2px(p.price.height);
                stackPanelPurchase.addControl(stackPanelPriceLine);
                var balanceTextBlock = ObjectBuilder.createTextBlock3D(this._mesh2d, "lockedVideoCoverBalance", "", p.price.value);
                stackPanelPriceLine.addControl(balanceTextBlock);
                const currencyImage = new Image3D("lockedVideoCoverCurrencyImage", p.price.currencyImage.src);
                currencyImage.widthInPixels = this._mesh2d.m2px(p.price.currencyImage.width);
                currencyImage.heightInPixels = this._mesh2d.m2px(p.price.currencyImage.height);
                stackPanelPriceLine.addControl(currencyImage);
            }
            addVerticalGapControl(stackPanelPurchase, lv.verticalGaps.gap32);
            const panelParent = new Container("lockedVideoCoverPanelParent");
            panelParent.widthInPixels = this._mesh2d.m2px(Math.max(p.unlockPanel.width, p.buyPanel.width));
            panelParent.heightInPixels = this._mesh2d.m2px(Math.max(p.unlockPanel.height, p.buyPanel.height));
            stackPanelPurchase.addControl(panelParent);
            {
                var panelUnlock = new Container("lockedVideoCoverUnlockButtonsPanel");
                panelUnlock.widthInPixels = this._mesh2d.m2px(p.unlockPanel.width);
                panelUnlock.heightInPixels = this._mesh2d.m2px(p.unlockPanel.height);
                panelParent.addControl(panelUnlock);
                const unlockButton = ButtonUtils.CreateImgButton("lockedVideoCoverUnlockButton", p.unlockPanel.unlockButton, this._mesh2d);
                unlockButton.widthInPixels = this._mesh2d.m2px(p.unlockPanel.unlockButton.width);
                unlockButton.heightInPixels = this._mesh2d.m2px(p.unlockPanel.unlockButton.height);
                unlockButton.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
                unlockButton.isPointerBlocker = true;
                unlockButton.onClickObserver.add(() => {
                    const appView = AppView.getInstance();
                    if (appView.appController.sdk.authManager.isAuthorized) {
                        setTimeout(() => this._showInternal("unlockToBuyTransition"), 0);
                        setTimeout(() => this._showInternal("buy"), screenDelayMillis);
                    }
                    else {
                        if (this._dialogs.login !== null) {
                            this._dialogs.login.show("signIn");
                        }
                        else {
                            setTimeout(async () => {
                                if (appView.loginAsync)
                                    await appView.loginAsync();
                            }, 0);
                        }
                    }
                });
                panelUnlock.addControl(unlockButton);
                const leaveButton = ButtonUtils.CreateImgButtonWithText("lockedVideoCoverLeaveButton", {
                    imageLayout: p.unlockPanel.leaveButton.rectangleLayout,
                    text: text.videoClip_lockedVideoCover_leaveButton,
                    textLayout: p.unlockPanel.leaveButton.textLayout,
                }, this._mesh2d);
                leaveButton.isPointerBlocker = true;
                leaveButton.onClickObserver.add(() => {
                    setTimeout(() => {
                        this._callbacks.leaveVideoClipRoom();
                    }, 0);
                });
                panelUnlock.addControl(leaveButton);
            }
            {
                var panelBuy = new Container("lockedVideoCoverBuyButtonsPanel");
                panelBuy.widthInPixels = this._mesh2d.m2px(p.buyPanel.width);
                panelBuy.heightInPixels = this._mesh2d.m2px(p.buyPanel.height);
                panelParent.addControl(panelBuy);
                const buyButton = ButtonUtils.CreateImgButtonWithText("lockedVideoCoverBuyButton", {
                    imageLayout: p.buyPanel.buyButton.imageLayout,
                    text: text.videoClip_lockedVideoCover_buyButton,
                    textLayout: p.buyPanel.buyButton.textLayout,
                }, this._mesh2d);
                buyButton.widthInPixels = this._mesh2d.m2px(p.buyPanel.buyButton.width);
                buyButton.heightInPixels = this._mesh2d.m2px(p.buyPanel.buyButton.height);
                buyButton.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
                buyButton.isPointerBlocker = true;
                panelBuy.addControl(buyButton);
                const cancelButton = ButtonUtils.CreateImgButtonWithText("lockedVideoCoverCancelButton", {
                    imageLayout: p.buyPanel.cancelButton.rectangleLayout,
                    text: text.videoClip_lockedVideoCover_cancelButton,
                    textLayout: p.buyPanel.cancelButton.textLayout,
                }, this._mesh2d);
                cancelButton.isPointerBlocker = true;
                cancelButton.onClickObserver.add(() => {
                    setTimeout(() => this._showInternal("unlockToBuyTransition"), 0);
                    setTimeout(() => this._showInternal("unlock"), screenDelayMillis);
                });
                panelBuy.addControl(cancelButton);
                buyButton.onClickObserver.add(() => setTimeout(async () => {
                    if (this._videoClip === null || !this._videoClip.product)
                        return;
                    if (this._videoClip.product.productPrice === undefined) {
                        console.error("The product price is not defined");
                        return;
                    }
                    const appView = AppView.getInstance();
                    this._showInternal("buyToFinalTransition");
                    try {
                        if (this._currentBalance < this._videoClip.product.productPrice) {
                            this._showInternal("buy");
                            this._dialogs.info.show("topUpWarning", {
                                actionCallback: () => {
                                    setTimeout(async () => {
                                        this._dialogs.info.hide();
                                        await appView.topUpAsync();
                                    }, 0);
                                },
                            });
                            return;
                        }
                        const itemPurchase = await this._callbacks.buyVideoClipAsync(this._videoClip.product.productId);
                        this._showInternal("final");
                        await delayAsync(thankyouDelayMillis);
                        this._callbacks.onPurchased(itemPurchase.item);
                    }
                    catch (e) {
                        console.error(e);
                        this._showInternal("buy");
                        appView.appController.notificationCenter?.showMessage(MessageType.error, e.message);
                    }
                }, 0));
            }
        }
        {
            const f = lv.final;
            var stackPanelFinal = new StackPanel("lockedVideoCoverStackPanelFinal");
            stackPanelFinal.isVertical = true;
            stackPanelFinal.horizontalAlignment = StackPanel.HORIZONTAL_ALIGNMENT_CENTER;
            stackPanelFinal.verticalAlignment = StackPanel.VERTICAL_ALIGNMENT_CENTER;
            this._mesh2d.texture.addControl(stackPanelFinal);
            const finalImage = new Image3D("lockedVideoCoverFinalImage", f.finalImage.src);
            finalImage.widthInPixels = this._mesh2d.m2px(f.finalImage.width);
            finalImage.heightInPixels = this._mesh2d.m2px(f.finalImage.height);
            stackPanelFinal.addControl(finalImage);
            addVerticalGapControl(stackPanelFinal, lv.verticalGaps.gap32);
            const finalLine1TextBlock = ObjectBuilder.createTextBlock3D(this._mesh2d, "lockedVideoCoverFinalLine1", text.videoClip_lockedVideoCover_finalLine1, f.finalLine1);
            stackPanelFinal.addControl(finalLine1TextBlock);
            addVerticalGapControl(stackPanelFinal, lv.verticalGaps.gap12);
            const finalLine2TextBlock = ObjectBuilder.createTextBlock3D(this._mesh2d, "lockedVideoCoverFinalLine2", text.videoClip_lockedVideoCover_finalLine2, f.finalLine2);
            stackPanelFinal.addControl(finalLine2TextBlock);
        }
        return {
            backgroundImage,
            panelPurchase: {
                self: stackPanelPurchase,
                titleTextBlock,
                statusControls,
                modelNicknameTextBlock,
                priceControls: {
                    stackPanel: stackPanelPriceLine,
                    priceTextBlock: balanceTextBlock,
                },
                panelUnlockButtons: panelUnlock,
                panelBuyButtons: panelBuy,
            },
            panelFinal: stackPanelFinal,
        };
    }
    _createDialogs() {
        const appView = AppView.getInstance();
        const rootNode = appView.guiRootNode;
        let loginDialog = null;
        if (appView.loginApiAuthAsync && appView.signupApiAuthAsync) {
            loginDialog = new LoginDialog(rootNode, {
                signInCallback: async (username, password) => {
                    return appView.loginApiAuthAsync(username, password);
                },
                signupCallback: async (email, username, password) => {
                    return appView.signupApiAuthAsync(email, username, password);
                },
                onShowCallback: () => {
                    this._callbacks.setMainMenuActivity(false, [MeshName.loginForm, MeshName.loginFormKeyboard, MeshName.progressBar, MeshName.notificationCenter]);
                },
                onHideCallback: () => {
                    this._callbacks.setMainMenuActivity(true, [MeshName.loginForm, MeshName.loginFormKeyboard, MeshName.progressBar, MeshName.notificationCenter]);
                },
            });
            loginDialog.hide();
        }
        else {
            // (async () => {
            //     if (appView.loginAsync) {
            //         await appView.loginAsync();
            //     }
            // })();
        }
        const infoDialog = new InfoDialog(rootNode, {
            onEveryShowCallback: () => {
                // InfoDialog mesh has `MeshName.loginForm` name!
                this._callbacks.setMainMenuActivity(false, [MeshName.loginForm, MeshName.loginFormKeyboard, MeshName.progressBar, MeshName.notificationCenter]);
            },
            onEveryHideCallback: () => {
                // InfoDialog mesh has `MeshName.loginForm` name!
                this._callbacks.setMainMenuActivity(true, [MeshName.loginForm, MeshName.loginFormKeyboard, MeshName.progressBar, MeshName.notificationCenter]);
            },
        });
        infoDialog.hide();
        return { login: loginDialog, info: infoDialog };
    }
    setPanelActivity(isActive, excludedMeshNames) {
        this._mesh2d.mesh.isPickable = isActive;
        Mesh2D.Meshes
            .filter(m => excludedMeshNames.find(x => m.name === x) === undefined)
            .forEach(m => m.mesh.colorMultiplier = isActive ? 1.0 : 0.4);
    }
    stateChanged(state, _isGuest) {
        this._controls.panelPurchase.statusControls.outlineContainer.color = stateToProfileImageColor[state] ?? "transparent";
    }
    setModelProfilePhoto(url) {
        this._controls.panelPurchase.statusControls.modelProfileImage.setSource(url, Env.lobby.gallery.defaultPreview.path);
    }
    outputClientBalance(tokens) {
        this._currentBalance = tokens;
    }
    outputVideoClip(videoClip) {
        this._videoClip = videoClip;
        const isVisible = videoClip !== null && !videoClip.isFree && videoClip.product?.isPurchased !== true;
        this._mesh2d.isVisible = isVisible;
        if (!isVisible)
            return;
        this._controls.backgroundImage.isVisible = videoClip.coverImageUrl !== undefined;
        this._controls.backgroundImage.source = videoClip.coverImageUrl ?? null;
        this._controls.panelPurchase.titleTextBlock.text = videoClip.title;
        this._controls.panelPurchase.modelNicknameTextBlock.text = videoClip.modelNickname;
        const price = videoClip.product?.productPrice ?? null;
        this._controls.panelPurchase.priceControls.stackPanel.isVisible = price !== null;
        this._controls.panelPurchase.priceControls.priceTextBlock.text = (price ?? 0).toLocaleString("ru", {});
    }
}
